import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Download } from "lucide-react";
import { ApplyNowModal } from "@/components/common/ApplyNowModal";

interface UniversalCalculatorProps {
  calculatorType: string;
}

export const UniversalCalculator = ({ calculatorType }: UniversalCalculatorProps) => {
  const [amount, setAmount] = useState("500000");
  const [rate, setRate] = useState("12");
  const [tenure, setTenure] = useState("24");
  const [sipAmount, setSipAmount] = useState("5000");
  const [investmentYears, setInvestmentYears] = useState("10");
  const [salary, setSalary] = useState("50000");
  const [exemptions, setExemptions] = useState("0");

  const calculateLoanEMI = (principal: number, rate: number, tenure: number) => {
    const r = rate / 12 / 100;
    if (r === 0) return Math.round(principal / tenure);
    const emi = (principal * r * Math.pow(1 + r, tenure)) / (Math.pow(1 + r, tenure) - 1);
    return Math.round(emi);
  };

  const calculateSIP = (monthlyAmount: number, rate: number, years: number) => {
    const r = rate / 12 / 100;
    const n = years * 12;
    const maturityAmount = monthlyAmount * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
    return Math.round(maturityAmount);
  };

  const calculateTax = (salary: number, exemptions: number) => {
    const taxableIncome = salary * 12 - exemptions;
    let tax = 0;
    
    if (taxableIncome <= 250000) tax = 0;
    else if (taxableIncome <= 500000) tax = (taxableIncome - 250000) * 0.05;
    else if (taxableIncome <= 1000000) tax = 12500 + (taxableIncome - 500000) * 0.2;
    else tax = 112500 + (taxableIncome - 1000000) * 0.3;
    
    return Math.round(tax);
  };

  const downloadEMIDetails = () => {
    const emi = calculateLoanEMI(parseFloat(amount), parseFloat(rate), parseFloat(tenure));
    const totalAmount = emi * parseFloat(tenure);
    const totalInterest = totalAmount - parseFloat(amount);
    
    const emiDetails = `
      EMI Calculator - ${calculatorType}
      =============================
      
      Loan Amount:       ₹${parseFloat(amount).toLocaleString()}
      Interest Rate:     ${rate}% p.a.
      Loan Tenure:       ${tenure} months (${(parseFloat(tenure)/12).toFixed(1)} years)
      
      EMI:               ₹${emi.toLocaleString()}
      Total Interest:    ₹${totalInterest.toLocaleString()}
      Total Payment:     ₹${totalAmount.toLocaleString()}
      
      Payment Schedule:
      - Principal:       ₹${parseFloat(amount).toLocaleString()}
      - Interest:        ₹${totalInterest.toLocaleString()}
      
      Generated on:      ${new Date().toLocaleString()}
      
      Note: This is an estimate. Actual terms may vary based on bank approval.
    `;
    
    // Create a Blob with the text
    const blob = new Blob([emiDetails], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary anchor element to trigger the download
    const a = document.createElement('a');
    a.href = url;
    a.download = `EMI_Details_${calculatorType.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderLoanCalculator = () => {
    const emi = calculateLoanEMI(parseFloat(amount), parseFloat(rate), parseFloat(tenure));
    const totalAmount = emi * parseFloat(tenure);
    const totalInterest = totalAmount - parseFloat(amount);

    return (
      <div className="grid lg:grid-cols-2 gap-8">
        <Card className="bg-slate-800/50 backdrop-blur-lg border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Calculator className="h-5 w-5 mr-2" />
              {calculatorType} Calculator
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white">Loan Amount (₹)</Label>
              <Input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
                placeholder="Enter loan amount"
              />
            </div>
            <div>
              <Label className="text-white">Interest Rate (% per annum)</Label>
              <Input
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
                placeholder="Enter interest rate"
              />
            </div>
            <div>
              <Label className="text-white">Tenure (Months)</Label>
              <Input
                value={tenure}
                onChange={(e) => setTenure(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
                placeholder="Enter tenure"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur-lg border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">EMI Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center p-4 bg-slate-700/30 rounded-lg">
              <div className="text-3xl font-bold text-blue-400">₹{emi.toLocaleString()}</div>
              <div className="text-slate-300">Monthly EMI</div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-slate-700/30 rounded-lg">
                <div className="text-xl font-bold text-green-400">₹{totalAmount.toLocaleString()}</div>
                <div className="text-slate-300 text-sm">Total Amount</div>
              </div>
              <div className="text-center p-3 bg-slate-700/30 rounded-lg">
                <div className="text-xl font-bold text-yellow-400">₹{totalInterest.toLocaleString()}</div>
                <div className="text-slate-300 text-sm">Total Interest</div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-white">
                <span>Principal:</span>
                <span>₹{parseFloat(amount).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-white">
                <span>Interest:</span>
                <span>₹{totalInterest.toLocaleString()}</span>
              </div>
            </div>

            <div className="w-full mt-4">
              <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
                <div className="w-full sm:flex-1">
                  <ApplyNowModal 
                    loanType={calculatorType.replace(' EMI', '')}
                  >
                    <Button 
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-xs sm:text-sm whitespace-nowrap h-9 sm:h-10"
                      size="sm"
                    >
                      Apply Now
                    </Button>
                  </ApplyNowModal>
                </div>
                <div className="w-full sm:flex-1">
                  <Button 
                    variant="outline" 
                    className="w-full border-blue-500 text-blue-400 hover:bg-blue-500/10 text-xs sm:text-sm whitespace-nowrap h-9 sm:h-10"
                    onClick={downloadEMIDetails}
                    size="sm"
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                    <span className="truncate">Download</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderSIPCalculator = () => {
    const maturityAmount = calculateSIP(parseFloat(sipAmount), parseFloat(rate), parseFloat(investmentYears));
    const totalInvested = parseFloat(sipAmount) * parseFloat(investmentYears) * 12;
    const gains = maturityAmount - totalInvested;

    return (
      <div className="grid lg:grid-cols-2 gap-8">
        <Card className="bg-slate-800/50 backdrop-blur-lg border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">SIP Calculator</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white">Monthly SIP Amount (₹)</Label>
              <Input
                value={sipAmount}
                onChange={(e) => setSipAmount(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-white">Expected Return (% per annum)</Label>
              <Input
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-white">Investment Period (Years)</Label>
              <Input
                value={investmentYears}
                onChange={(e) => setInvestmentYears(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-600/20 to-blue-600/20 backdrop-blur-lg border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Investment Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center p-4 bg-slate-700/30 rounded-lg">
              <div className="text-3xl font-bold text-green-400">₹{maturityAmount.toLocaleString()}</div>
              <div className="text-slate-300">Maturity Amount</div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-slate-700/30 rounded-lg">
                <div className="text-xl font-bold text-blue-400">₹{totalInvested.toLocaleString()}</div>
                <div className="text-slate-300 text-sm">Total Invested</div>
              </div>
              <div className="text-center p-3 bg-slate-700/30 rounded-lg">
                <div className="text-xl font-bold text-yellow-400">₹{gains.toLocaleString()}</div>
                <div className="text-slate-300 text-sm">Total Gains</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderTaxCalculator = () => {
    const tax = calculateTax(parseFloat(salary), parseFloat(exemptions));
    const netSalary = parseFloat(salary) * 12 - tax;

    return (
      <div className="grid lg:grid-cols-2 gap-8">
        <Card className="bg-slate-800/50 backdrop-blur-lg border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Income Tax Calculator</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-white">Monthly Salary (₹)</Label>
              <Input
                value={salary}
                onChange={(e) => setSalary(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-white">Annual Exemptions (₹)</Label>
              <Input
                value={exemptions}
                onChange={(e) => setExemptions(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-600/20 to-orange-600/20 backdrop-blur-lg border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Tax Calculation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center p-4 bg-slate-700/30 rounded-lg">
              <div className="text-3xl font-bold text-red-400">₹{tax.toLocaleString()}</div>
              <div className="text-slate-300">Annual Tax</div>
            </div>
            
            <div className="text-center p-3 bg-slate-700/30 rounded-lg">
              <div className="text-xl font-bold text-green-400">₹{netSalary.toLocaleString()}</div>
              <div className="text-slate-300 text-sm">Net Annual Income</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (calculatorType.includes('SIP') || calculatorType.includes('Investment')) {
    return renderSIPCalculator();
  } else if (calculatorType.includes('Tax') || calculatorType.includes('Income')) {
    return renderTaxCalculator();
  } else {
    return renderLoanCalculator();
  }
};
